<template>
  <ModalComponent
    v-if="isVisible"
    size="lg"
    :submit-button-text="isMuted ? 'Unmute' : 'Mute device'"
    :submit="handleSubmit"
    :submit-disabled="isProcessing"
    @close="$emit('close')"
    @set-force-toggle="(fn: (v: boolean) => void) => fn(true)"
  >
    <template #title>
      {{ isMuted ? "Unmute device" : "Mute device" }}
    </template>
    <template #body>
      <p class="text-secondary fs-9 mb-3">
        <strong>{{ device.name ?? `Device #${device.id}` }}</strong>
      </p>

      <template v-if="isMuted">
        <dl class="row mb-0">
          <dt class="col-4">Muted by</dt>
          <dd class="col-8">{{ device.mute!.muted_by }}</dd>
          <dt class="col-4">Muted from</dt>
          <dd class="col-8">{{ formatDate(device.mute!.muted_at) }}</dd>
          <dt class="col-4">Muted until</dt>
          <dd class="col-8">{{ formatDate(device.mute!.muted_until) }}</dd>
          <template v-if="device.mute!.muted_reason">
            <dt class="col-4">Reason</dt>
            <dd class="col-8">{{ device.mute!.muted_reason }}</dd>
          </template>
        </dl>
      </template>

      <template v-else>
        <div class="mb-3">
          <label class="form-label">Mute for (days)</label>
          <input
            v-model.number="form.days"
            type="number"
            class="form-control"
            min="1"
            @keydown.enter.prevent="handleSubmit"
          />
        </div>
        <div class="mb-3">
          <label class="form-label"
            >Reason <span class="text-secondary">(optional)</span></label
          >
          <input
            ref="reasonInput"
            v-model="form.reason"
            type="text"
            class="form-control"
            placeholder="e.g. Device offline for maintenance"
            @keydown.enter.prevent="handleSubmit"
          />
        </div>
      </template>

      <hr />

      <h6 class="mb-2">Mute history</h6>
      <p v-if="historyLoading" class="text-secondary fs-9 mb-0">Loading…</p>
      <p
        v-else-if="history.length === 0"
        class="text-secondary fs-9 mb-0"
        data-testid="mute-history-empty"
      >
        This device has never been muted.
      </p>
      <div v-else class="table-responsive">
        <table class="table table-sm fs-9 mb-0" data-testid="mute-history">
          <thead>
            <tr>
              <th>Period</th>
              <th>Muted by</th>
              <th>Reason</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="entry in history"
              :key="entry.id"
              :class="{ 'table-warning': entry.status === 'active' }"
            >
              <td class="text-nowrap">
                {{ formatDate(entry.muted_at) }} –
                {{ formatDate(entry.muted_until) }}
              </td>
              <td>{{ entry.muted_by }}</td>
              <td>{{ entry.muted_reason ?? "—" }}</td>
              <td>
                <span
                  class="badge"
                  :class="statusBadgeClass(entry.status)"
                  :title="statusTitle(entry)"
                >
                  {{ entry.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <p
          v-if="history.length >= MUTE_HISTORY_LIMIT"
          class="text-secondary fs-10 mb-0"
        >
          Showing the {{ MUTE_HISTORY_LIMIT }} most recent entries.
        </p>
      </div>
    </template>
    <template #footer>
      <template v-if="isMuted">
        <button
          class="btn btn-primary"
          type="button"
          :disabled="isProcessing"
          @click="handleSubmit"
        >
          Unmute
        </button>
      </template>
      <template v-else>
        <button
          class="btn btn-outline-primary"
          type="button"
          :disabled="isProcessing"
          data-testid="mute-365"
          @click="mute(365)"
        >
          Mute for 365 days
        </button>
        <button
          class="btn btn-primary"
          type="button"
          :disabled="isProcessing"
          @click="handleSubmit"
        >
          Mute device
        </button>
      </template>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type {
  DeviceMuteHistoryEntry,
  DeviceMuteHistoryResponse,
  DeviceMuteInfo,
  DeviceMuteStatus,
  IssuanceDashboardDevice,
} from "@/types";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";

// Mirrors the backend cap so the "showing N most recent" note only appears when truncation is possible.
const MUTE_HISTORY_LIMIT = 10;

const props = defineProps<{
  isVisible: boolean;
  device: IssuanceDashboardDevice;
}>();

const emit = defineEmits<{
  close: [];
  "mute-changed": [mute: DeviceMuteInfo | null];
}>();

const { globalErrorHandler } = useGlobalHandlers();
const isProcessing = ref(false);

const form = ref({ days: 90, reason: "" });
const reasonInput = useTemplateRef<HTMLInputElement>("reasonInput");

const history = ref<DeviceMuteHistoryEntry[]>([]);
const historyLoading = ref(false);

const isMuted = computed(
  () =>
    props.device.mute !== null &&
    new Date(props.device.mute.muted_until) > new Date()
);

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      form.value = { days: 90, reason: "" };
      void loadHistory();
    }
  }
);

onMounted(() => {
  void loadHistory();
  if (isMuted.value) {
    (
      document.querySelector(".modal .btn-primary") as HTMLElement | null
    )?.focus();
  } else {
    reasonInput.value?.focus();
  }
});

const formatDate = (iso: string): string => iso.slice(0, 10);

const statusBadgeClass = (status: DeviceMuteStatus): string =>
  ({
    active: "bg-warning text-dark",
    expired: "bg-secondary",
    unmuted: "bg-light text-dark border",
  })[status];

const statusTitle = (entry: DeviceMuteHistoryEntry): string =>
  entry.status === "unmuted" && entry.unmuted_at
    ? `Unmuted ${formatDate(entry.unmuted_at)} by ${entry.unmuted_by ?? "unknown"}`
    : "";

async function loadHistory() {
  historyLoading.value = true;
  try {
    const res = await goSolidApi.get<DeviceMuteHistoryResponse>(
      `/api/admin/issuance/devices/${props.device.id}/mutes`
    );
    history.value = res.data.history;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    historyLoading.value = false;
  }
}

async function mute(days: number) {
  isProcessing.value = true;
  try {
    const res = await goSolidApi.post<DeviceMuteInfo>(
      `/api/admin/issuance/devices/${props.device.id}/mute`,
      { days, reason: form.value.reason || null }
    );
    emit("mute-changed", res.data);
    emit("close");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isProcessing.value = false;
  }
}

async function unmute() {
  isProcessing.value = true;
  try {
    await goSolidApi.delete(
      `/api/admin/issuance/devices/${props.device.id}/mute`
    );
    emit("mute-changed", null);
    emit("close");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isProcessing.value = false;
  }
}

async function handleSubmit() {
  if (isMuted.value) {
    await unmute();
  } else {
    await mute(form.value.days);
  }
}
</script>
